import React, { Fragment } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import { useTheme } from "@mui/material/styles";
import QuickSearchToolbar from "views/components/QuickSearchToolbar";
import { isPermissionAllow } from "@core/utils/IsPermissionAllow";
import Typography from "@mui/material/Typography";
import moment from "moment";

/**
 * MembershipPriceList
 *
 * Displays a paginated DataGrid showing the full history of all membership
 * prices. Only one record will be marked Active at any given time.
 *
 * Props:
 *   priceList     - array of pricing records
 *   pageSize      - rows per page
 *   pageNumber    - current page index (0-based)
 *   totalRecord   - total count for server-side pagination
 *   setPageSize   - setter for page size
 *   setPageNumber - setter for page number
 *   handleEdit    - function to open the edit drawer
 *   handleMembershipStatusChange - function to toggle active record by ID
 *   permission    - permission object for sub-admin
 *   role          - "1" (super admin) | "2" (sub admin)
 */
const MembershipPriceList = ({
  priceList,
  pageSize,
  pageNumber,
  totalRecord,
  setPageSize,
  setPageNumber,
  handleEdit,
  handleMembershipStatusChange,
  permission,
  role,
}) => {
  const theme = useTheme();

  const handleClick = (id) => {
    if (handleMembershipStatusChange) {
      handleMembershipStatusChange(id);
    }
  };

  const columns = [
    {
      flex: 0.3,
      minWidth: 60,
      field: "index",
      headerName: "Index",
    },
    {
      flex: 1,
      minWidth: 120,
      field: "amount",
      headerName: "Amount (AUD)",
      renderCell: ({ row }) => `${Number(row.amount).toFixed(2)}`,
    },
    {
      flex: 1,
      minWidth: 100,
      field: "is_active",
      headerName: "Status",
      renderCell: ({ row }) =>
        row.is_active ? (
          <Chip label="Active" color="success" size="small" />
        ) : (
          <Chip label="Inactive" color="default" size="small" />
        ),
    },
    {
      flex: 1.5,
      minWidth: 150,
      field: "created_at",
      headerName: "Created Date",
      renderCell: ({ row }) => {
        const formatDate = moment(row.created_at).format("DD-MM-yyyy hh:mm A");
        return (
          <Typography sx={{ fontSize: "0.9rem !important" }}>
            {formatDate}
          </Typography>
        );
      },
    },
    {
      flex: 1.3,
      minWidth: 150,
      field: "updated_at",
      headerName: "Updated Date",
      renderCell: ({ row }) => {
        const formatDate = moment(row.updated_at).format("DD-MM-yyyy hh:mm A");
        return (
          <Typography sx={{ fontSize: "0.9rem !important" }}>
            {formatDate}
          </Typography>
        );
      },
    },
    {
      flex: 0.8,
      minWidth: 80,
      field: "action",
      headerName: "Actions",
      renderCell: ({ row }) => {
        if (row.is_active) return null
        return (
          <Switch
            checked={row.is_active}
            size={"small"}
            style={{ color: theme.palette.primary.light }}
            onChange={() => handleClick(row.id)}
          />
        );
      },
    },
  ];

  const rows = priceList.map((item, index) => ({
    index: index + pageSize * pageNumber + 1,
    ...item,
  }));

  return (
    <Fragment>
      <Card sx={{ width: "100%", overflowInline: "hidden" }}>
        <DataGrid
          hideFooterSelectedRowCount
          autoHeight
          localeText={{ noRowsLabel: "No membership prices found" }}
          rowCount={Number(totalRecord)}
          page={pageNumber}
          columns={columns}
          rows={rows}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          pagination
          paginationMode="server"
          components={{ Toolbar: QuickSearchToolbar }}
          onPageChange={(newPage) => setPageNumber(newPage)}
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          disableSelectionOnClick
          componentsProps={{
            toolbar: {
              value: "",
              clearSearch: () => { },
              onChange: () => { },
              handleOpen: isPermissionAllow(permission.edit, role)
                ? () => handleEdit(null)
                : undefined,
              title: isPermissionAllow(permission.edit, role)
                ? "Add Membership Price"
                : "",
              isSearch: false,
              selectedRows: [],
              multiDelete: false,
            },
          }}
          sx={{
            ".MuiTablePagination-displayedRows": { marginTop: "1rem" },
            ".MuiTablePagination-selectLabel": { marginTop: "1.1rem" },
            ".MuiInputBase-root": { marginTop: "0.2rem" },
          }}
        />
      </Card>
    </Fragment>
  );
};

export default MembershipPriceList;
